import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DepositDetailDocumentFragment, useDepositDetailDocumentsQuery } from 'src/api/api.generated'
import { HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { useRequiredProperty, useTenant } from 'src/app/hooks/context/context.hook'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Link, Typography, Stack } from '@vapor/material'
import { keysOf } from 'src/utils/utils'
import getFiscalTypeDescription from '../documents-utils'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import CheckoutDocumentForm from '../checkout-document/checkout-document-form'
import RequiredProperty from 'src/app/components/required-property/required-property'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'

type DepositDetailDrawerProps = {
  posDepositDocumentIds: string[]
  open: boolean
  onClose: () => void
}

const Fields = keysOf<DepositDetailDocumentFragment>()

function DepositDetailDrawer(props: DepositDetailDrawerProps) {
  const { open, onClose, posDepositDocumentIds } = props
  const { t } = useTranslation()
  const { id: tenantId, currency } = useTenant()
  const { id: propertyId } = useRequiredProperty()
  const { formatCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()

  const [checkoutDocumentId, setCheckoutDocumentId] = useState<string | null>(null)
  const [referenceDocumentId, setReferenceDocumentId] = useState<string | null>(null)

  const { data, loading } = useDepositDetailDocumentsQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        id: { in: posDepositDocumentIds },
      },
    },
  })

  const onDocumentClick = (documentId: string) => {
    setCheckoutDocumentId(documentId)
  }

  const columns: GridColDef<DepositDetailDocumentFragment>[] = [
    {
      field: Fields('numberExtended'),
      headerName: t('pms.accounting.documents.progressive'),
      disableColumnMenu: true,
      width: 150,
      renderCell: (params) => {
        const row = params.row
        return <Link onClick={() => onDocumentClick(row.id)}>{params.formattedValue}</Link>
      },
    },
    {
      field: Fields('fiscalType'),
      headerName: t('pms.accounting.documents.type'),
      disableColumnMenu: true,
      renderCell: (params) => <Typography variant="caption">{getFiscalTypeDescription(params.row.fiscalType, t).toUpperCase()}</Typography>,
    },
    {
      field: Fields('documentDate'),
      headerName: t('common.date'),
      disableColumnMenu: true,
      width: 90,
      renderCell: (params) => {
        return formatDate(params.formattedValue)
      },
    },
    {
      field: Fields('totalAmount'),
      headerName: t('pms.accounting.documents.total_amount'),
      disableColumnMenu: true,
      width: 140,
      type: 'number',
      renderCell: (params) => {
        return (
          <Stack flexGrow={1} textAlign={'end'}>
            {formatCurrency(params.row.totalAmount, params.row.currency)}
          </Stack>
        )
      },
    },
    {
      field: Fields('residualAmount'),
      headerName: t('pms.booking.reservation.deposits.residual'),
      disableColumnMenu: true,
      width: 140,
      type: 'number',
      renderCell: (params) => {
        return (
          <Stack flexGrow={1} textAlign={'end'}>
            {formatCurrency(params.row.residualAmount ?? 0, params.row.currency)}
          </Stack>
        )
      },
    },
  ]

  return (
    <FormLayout //
      title={t('pms.booking.reservation.checkout_document.deposits_title')}
      appearance={{ type: 'DRAWER', open, onClose }}
      toolbar={
        <FormLayoutDefaultToolbar
          cancelButton={{
            text: t('commons.close'),
            clickHandler: onClose,
          }}
        />
      }
    >
      <HicDataGridPro //
        columns={columns}
        rows={data?.documents ?? []}
        hideFooter
      />
      {checkoutDocumentId !== null && (
        <CheckoutDocumentForm //
          propertyId={propertyId}
          open={checkoutDocumentId !== null}
          onClose={() => setCheckoutDocumentId(null)}
          documentId={checkoutDocumentId}
          onOpenReferenceDocument={(documentId: string) => setReferenceDocumentId(documentId)}
          onCreditNoteSuccess={(creditNoteId, manualDocumentId) => setCheckoutDocumentId(manualDocumentId ? manualDocumentId : creditNoteId)}
          refetchQueries={['Documents']}
          onSuccess={({ documentId }) => {
            setCheckoutDocumentId(null)
            //setPrintDocumentId(documentId)
          }}
        />
      )}
    </FormLayout>
  )
}

export default (props: DepositDetailDrawerProps) => (
  <RequiredProperty>
    <DepositDetailDrawer {...props} />
  </RequiredProperty>
)
